import React, { useState } from "react";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import {
  Wrapper,
  Card,
  IconBox,
  Title,
  Description,
  EmailBox,
  SecondDescription,
  FormSection,
  InfoRow,
  InfoBox,
  InfoTitle,
  InfoDescription,
} from "./ConfirmRegistrationStep.elements";
import { useTranslation } from "react-i18next";
import { ReactComponent as ApproveCheckIcon } from "../../assets/icons/approve.svg";
import { TextInput } from "../TextInput/TextInput";
import { useForm } from "react-hook-form";
import { ButtonGhost, ButtonPrimary } from "../Button/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  DispatchType,
  registrationActions,
  registrationSelectors,
  uiSelectors,
} from "../../redux";
import { showModal } from "../../redux/modal";
import ErrorModalContent from "../ErrorModalContent";
import { Spinner } from "../Spinner/Spinner";
export type ConfirmRegistrationStepProps = {
  email: string;
  country: string;
  onEditEmail: () => void;
  onNext?: (confirmationCode: string) => void;
};

export default function ConfirmRegistrationStep({
  email,
  country,
  onEditEmail,
  onNext,
}: ConfirmRegistrationStepProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<DispatchType>();
  const language = useSelector(uiSelectors.getLanguage);
  const registrationLoading = useSelector(registrationSelectors.getLoading);
  const [info1Sent, setInfo1Sent] = useState(false);

  async function handleInfo1Click() {
    try {
      await dispatch(
        registrationActions.generateRegistrationToken({
          language: language.toLowerCase(),
          country: country,
          email: email,
          isCompany: true,
          captchaToken: "",
          promoNotifications: false,
        })
      )
        .unwrap()
        .then(() => {
          setInfo1Sent(true);
        })
        .catch((error) => {
          dispatch(
            showModal({
              title: t("common.error.title"),
              content: <ErrorModalContent message={error} />,
            })
          );
        });
    } catch (error) {
      console.error("Registration token generation failed:", error);
    }
  }
  const { register, handleSubmit, watch } = useForm<{ code: string }>({
    defaultValues: { code: "" },
  });
  const code = watch("code");
  const isCodeValid = code.length === 8;

  async function onSubmit() {
    if (isCodeValid && onNext) {
      onNext(code);
    }
  }

  return (
    <Wrapper>
      <Card>
        <IconBox>
          <ApproveCheckIcon />
        </IconBox>
        <Title>{t("registration.confirmStep.title")}</Title>
        <Description>{t("registration.confirmStep.description1")}</Description>
        <EmailBox>{email}</EmailBox>
        <SecondDescription>
          {t("registration.confirmStep.description2")}
        </SecondDescription>
        <ButtonGhost onClick={onEditEmail}>
          {t("registration.confirmStep.editEmail")}
        </ButtonGhost>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <FormSection>
            <TextInput
              label={t("registration.confirmStep.codeLabel")}
              register={register}
              name="code"
              placeholder={t("registration.confirmStep.codePlaceholder")}
            />
            <ButtonPrimary
              disabled={!isCodeValid}
              onClick={handleSubmit(onSubmit)}
            >
              {t("registration.confirmStep.next")}
            </ButtonPrimary>
          </FormSection>
        </form>
      </Card>
      <InfoRow>
        <InfoBox>
          <ApproveCheckIcon />
          <InfoTitle>{t("registration.confirmStep.info1Title")}</InfoTitle>
          <InfoDescription>
            {t("registration.confirmStep.info1Desc")}
          </InfoDescription>
          <SwitchTransition mode="out-in">
            <CSSTransition
              key={info1Sent ? "sent" : "default"}
              timeout={300}
              classNames="fade"
            >
              {info1Sent ? (
                <ButtonGhost disabled>
                  {t("registration.confirmStep.info1SentButton")}
                </ButtonGhost>
              ) : (
                <ButtonGhost onClick={handleInfo1Click}>
                  {registrationLoading && <Spinner size="small" />}
                  {t("registration.confirmStep.info1RequestButton")}
                </ButtonGhost>
              )}
            </CSSTransition>
          </SwitchTransition>
        </InfoBox>
        <InfoBox>
          <ApproveCheckIcon />
          <InfoTitle>{t("registration.confirmStep.info2Title")}</InfoTitle>
          <InfoDescription>
            {t("registration.confirmStep.info2Desc")}
          </InfoDescription>
        </InfoBox>
      </InfoRow>
    </Wrapper>
  );
}
